import React, { ReactNode, useState, useRef, useImperativeHandle } from "react";
import { CheckIcon } from "@/icons";
import {
  Command,
  CommandItem,
  CommandEmpty,
  CommandList,
  CommandInput,
  CommandGroup,
} from "./command";
import { cn } from "@/utils/tailwind";
import { Input, InputProps } from "./input";
import { Popover, PopoverContent } from "./popover";
import { PopoverTrigger } from "@radix-ui/react-popover";
import { useOnChangeEffect } from "@/utils/hooks";

interface Item<T> {
  value: T;
  label: string;
}

const findItem = <T,>(
  items: ReadonlyArray<Item<T>>,
  value?: T | null,
): Item<T> | undefined =>
  value === undefined ? undefined : items.find((item) => item.value === value);

interface ComboboxProps<T> extends Omit<
  InputProps,
  "onFocus" | "onBlur" | "defaultValue" | "value" | "onChange"
> {
  items: ReadonlyArray<Item<T>>;
  defaultValue?: T | null;
  onChange?: (value: T | null) => void;
  value?: T | null;
  emptyMessage: ReactNode;
  openOnFocus?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
}

const ComboboxInner = <T,>(
  {
    items,
    emptyMessage,
    defaultValue,
    value: passedValue,
    onChange,
    openOnFocus = false,
    onFocus,
    onBlur,
    inputProps: { className: inputClassName, ...inputProps } = {},
    className,
    ...props
  }: ComboboxProps<T>,
  forwardedRef: React.Ref<HTMLInputElement>,
) => {
  const [value, setValue] = useState(passedValue || defaultValue);
  const [selected, setSelected] = useState(findItem(items, value));
  const [open, setOpen] = useState(false);
  const [internalOpenOnFocus, setInternalOpenOnFocus] = useState(openOnFocus);
  const inputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement);

  useOnChangeEffect(
    passedValue,
    React.useCallback(
      (passedValue) => {
        setValue(passedValue);
      },
      [setValue],
    ),
  );

  useOnChangeEffect(
    value,
    React.useCallback(
      (value) => {
        const selected = findItem(items, value);
        setSelected(selected);
        onChange && onChange(selected?.value || null);
      },
      [items, setSelected, onChange],
    ),
  );

  return (
    <Popover
      open={open}
      onOpenChange={(newOpen) => {
        setOpen(newOpen);
        if (newOpen) {
          onFocus && onFocus();
        } else {
          onBlur && onBlur();
        }
      }}
      modal={true}
    >
      <PopoverTrigger asChild>
        <Input
          role="combobox"
          type="text"
          readOnly
          aria-expanded={open}
          value={selected?.label || ""}
          className={cn("hover:cursor-pointer selection:bg-white", className)}
          inputProps={{
            className: cn("text-left pointer-events-none", inputClassName),
            ...inputProps,
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              setOpen(true);
            }
          }}
          onFocus={() => {
            if (internalOpenOnFocus) {
              setOpen(true);
            }
          }}
          onBlur={(event) => {
            if (!contentRef.current?.contains(event.relatedTarget)) {
              setInternalOpenOnFocus(openOnFocus);
              setOpen(false);
            }
          }}
          {...props}
          ref={inputRef}
        />
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-(--radix-popper-anchor-width)!"
        align="start"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          setTimeout(() => {
            inputRef.current?.focus();
          }, 0);
        }}
        ref={contentRef}
      >
        <Command loop>
          <CommandInput placeholder="Type to search…" />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {selected && (
                <CommandItem
                  onSelect={() => {
                    setValue(null);
                    setOpen(false);
                    onBlur && onBlur();
                  }}
                >
                  <CheckIcon className="mr-2 h-4 2-4" />
                  {selected.label}
                </CommandItem>
              )}
              {items
                .filter((item) => selected?.value != item.value)
                .map((item) => {
                  return (
                    <CommandItem
                      key={item.label}
                      onSelect={() => {
                        setValue(item.value);
                        setOpen(false);
                        onBlur && onBlur();
                      }}
                    >
                      <div className={cn("mr-2 h-4 w-4")} />
                      {item.label}
                    </CommandItem>
                  );
                })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const Combobox = React.forwardRef(ComboboxInner) as <
  T extends string | number,
>(
  p: ComboboxProps<T> & { ref?: React.Ref<HTMLInputElement> },
) => React.ReactElement;
